/**
 * Staff "admin box" route bundle — engineering and management tooling
 * (firmware matrix/upgrades, queue + station status, warranty/RMA fees,
 * tamper + activation queries, ethernet stats, system notices). Every
 * page is staff-gated and lazy-loaded. Unlike billing/sales/services/
 * returns, this area ships in every build (no exclude flag).
 *
 * Contains:
 * - adminRoutes — array of <Route> elements
 */
import React from 'react';
import { Route } from 'react-router-dom';

import {
  determineCanUpgradeFirmware,
  determineIsAccountingUser,
  determineIsStaff,
} from '@sophocles/router/checkCredentials';
import {
  staffActivationQueryPath,
  staffBillingQuickbooksPath,
  staffEthernetStatsPath,
  staffExcessiveAlarmsPath,
  staffFirmwareMatrixPath,
  staffFirmwareUpgradeBatchPath,
  staffFirmwareUpgradeCreatePath,
  staffFirmwareUpgradesPath,
  staffHomePath,
  staffIssueCategoriesPath,
  staffQueueStatusPath,
  staffRmaFeePath,
  staffStationPath,
  staffSystemNoticePath,
  staffSystemNoticesPath,
  staffTamperIssuesPath,
  staffWarrantyFeePath,
  staffWarrantyStatusPath,
} from '@sophocles/router/paths';
import RequireAuth from '@sophocles/router/RequireAuth';

// Staff "admin box" pages — engineering + management tooling, all
// staff-gated. Lazy-loaded and grouped into adminRoutes to match the
// billing/services/sales/returns route bundles.
const AdminHomePage = React.lazy(() => import('@sophocles/views/admin/AdminDashboard'));
const SystemNoticesGridView = React.lazy(() => import('@sophocles/views/admin/SystemNoticesGridView'));
const SystemNoticeDetailView = React.lazy(() => import('@sophocles/views/admin/SystemNoticeDetailView'));
// engineering
const FirmwareMatrixPage = React.lazy(
  () => import('@sophocles/views/admin/engineering/firmwareMatrix/FirmwareMatrixPage'),
);
const FirmwareUpgradeGridView = React.lazy(
  () => import('@sophocles/views/admin/engineering/firmwareUpgrades/FirmwareUpgradeGridView'),
);
const FirmwareUpgradeBulkPage = React.lazy(
  () => import('@sophocles/views/admin/engineering/firmwareUpgrades/FirmwareUpgradeBulkPage'),
);
const FirmwareUpgradeBatchDetailPage = React.lazy(
  () => import('@sophocles/views/admin/engineering/firmwareUpgrades/FirmwareUpgradeBatchDetailPage'),
);
const QueueStatusPage = React.lazy(() => import('@sophocles/views/admin/engineering/queueStatus/QueueStatusPage'));
const StationContainer = React.lazy(() => import('@sophocles/views/admin/engineering/stationInfo/StationPage'));
const TamperIssuePage = React.lazy(() => import('@sophocles/views/admin/engineering/tamperIssue/TamperIssuePage'));
const ActivationQueryView = React.lazy(() => import('@sophocles/views/admin/engineering/ActivationQueryView'));
const EthernetStatsView = React.lazy(() => import('@sophocles/views/admin/engineering/ethernet/EthernetStatsView'));
const ExcessiveAlarmSitesView = React.lazy(() => import('@sophocles/views/sites/ExcessiveAlarmSitesView'));
// management
const IssueCategoryContainer = React.lazy(
  () => import('@sophocles/views/admin/management/issueCategories/IssueCategoryPage'),
);
const RMAFeeContainer = React.lazy(() => import('@sophocles/views/admin/management/rmaFee/RMAFeePage'));
const WarrantyFeeContainer = React.lazy(() => import('@sophocles/views/admin/management/warrantyFee/WarrantyFeePage'));
const WarrantyStatusContainer = React.lazy(
  () => import('@sophocles/views/admin/management/warrantyStatus/WarrantyStatusPage'),
);
const QuickBooksCallback = React.lazy(() => import('@sophocles/views/billing/QuickBooksCallbackView'));

export const adminRoutes = [
  <Route
    key={staffHomePath.pattern}
    path={staffHomePath.pattern}
    element={<RequireAuth component={AdminHomePage} onEnter={determineIsStaff} />}
  />,
  <Route
    key={staffFirmwareMatrixPath.pattern}
    path={staffFirmwareMatrixPath.pattern}
    element={<RequireAuth component={FirmwareMatrixPage} onEnter={determineIsStaff} />}
  />,
  <Route
    key={staffFirmwareUpgradesPath.pattern}
    path={staffFirmwareUpgradesPath.pattern}
    element={<RequireAuth component={FirmwareUpgradeGridView} onEnter={determineCanUpgradeFirmware} />}
  />,
  <Route
    key={staffFirmwareUpgradeCreatePath.pattern}
    path={staffFirmwareUpgradeCreatePath.pattern}
    element={<RequireAuth component={FirmwareUpgradeBulkPage} onEnter={determineCanUpgradeFirmware} />}
  />,
  <Route
    key={staffFirmwareUpgradeBatchPath.pattern}
    path={staffFirmwareUpgradeBatchPath.pattern}
    element={<RequireAuth component={FirmwareUpgradeBatchDetailPage} onEnter={determineCanUpgradeFirmware} />}
  />,
  <Route
    key={staffQueueStatusPath.pattern}
    path={staffQueueStatusPath.pattern}
    element={<RequireAuth component={QueueStatusPage} onEnter={determineIsStaff} />}
  />,
  <Route
    key={staffWarrantyStatusPath.pattern}
    path={staffWarrantyStatusPath.pattern}
    element={<RequireAuth component={WarrantyStatusContainer} onEnter={determineIsStaff} />}
  />,
  <Route
    key={staffBillingQuickbooksPath.pattern}
    path={staffBillingQuickbooksPath.pattern}
    element={<RequireAuth component={QuickBooksCallback} onEnter={determineIsAccountingUser} />}
  />,
  <Route
    key={staffWarrantyFeePath.pattern}
    path={staffWarrantyFeePath.pattern}
    element={<RequireAuth component={WarrantyFeeContainer} onEnter={determineIsStaff} />}
  />,
  <Route
    key={staffRmaFeePath.pattern}
    path={staffRmaFeePath.pattern}
    element={<RequireAuth component={RMAFeeContainer} onEnter={determineIsStaff} />}
  />,
  <Route
    key={staffStationPath.pattern}
    path={staffStationPath.pattern}
    element={<RequireAuth component={StationContainer} onEnter={determineIsStaff} />}
  />,
  <Route
    key={staffIssueCategoriesPath.pattern}
    path={staffIssueCategoriesPath.pattern}
    element={<RequireAuth component={IssueCategoryContainer} onEnter={determineIsStaff} />}
  />,
  <Route
    key={staffSystemNoticesPath.pattern}
    path={staffSystemNoticesPath.pattern}
    element={<RequireAuth component={SystemNoticesGridView} onEnter={determineIsStaff} />}
  />,
  <Route
    key={staffSystemNoticePath.pattern}
    path={staffSystemNoticePath.pattern}
    element={<RequireAuth component={SystemNoticeDetailView} onEnter={determineIsStaff} />}
  />,
  <Route
    key={staffTamperIssuesPath.pattern}
    path={staffTamperIssuesPath.pattern}
    element={<RequireAuth component={TamperIssuePage} onEnter={determineIsStaff} />}
  />,
  <Route
    key={staffActivationQueryPath.pattern}
    path={staffActivationQueryPath.pattern}
    element={<RequireAuth component={ActivationQueryView} onEnter={determineIsStaff} />}
  />,
  <Route
    key={staffExcessiveAlarmsPath.pattern}
    path={staffExcessiveAlarmsPath.pattern}
    element={<RequireAuth component={ExcessiveAlarmSitesView} onEnter={determineIsStaff} />}
  />,
  <Route
    key={staffEthernetStatsPath.pattern}
    path={staffEthernetStatsPath.pattern}
    element={<RequireAuth component={EthernetStatsView} onEnter={determineIsStaff} />}
  />,
];
